'use client';

import {
  Box,
  Container,
  Divider,
  Flex,
  Heading,
  HStack,
  Stack,
  Text,
} from '@chakra-ui/react';
import cn from 'classnames';
import React from 'react';
import { useRef, useState } from 'react';

import ButtonCustom from '@/app/_components/button/ButtonCustom';
import CustomImage from '@/app/_components/custom-image/CustomImage';
import Image from '@/app/_components/Image';
import LinkCustom from '@/app/_components/link/LinkCustom';
import { PriceFormat } from '@/app/_components/price-format/price-format';
import PauseIconDisable from '@/app/_icons/pause-gray.svg';
import PauseIcon from '@/app/_icons/pause.svg';
import PlayIconDisable from '@/app/_icons/play-gray.svg';
import PlayIcon from '@/app/_icons/play.svg';
import { useOpenFloatingCTA } from '@/hooks/floating-cta.hook';
import { getImageLink } from '@/services/link.service';
import { SpecialDetails } from '@/services/SpecialsService.service';
import { BannerCarousel } from '@/types/banners/banners.types';
import { ImageTransformationKeys } from '@/types/files/files.types';
import { NewCarData } from '@/types/new-cars/new-cars.types';

import Styles from './Banner.module.scss';

type BannerProps = {
  model?: string;
  align?: string;
  banner: BannerCarousel;
  special: SpecialDetails;
  newCarDetails?: NewCarData;
  showOverlay?: boolean;
};

const imageKey: ImageTransformationKeys = {
  mobileKey: 'image-560-325',
  desktopKey: 'image-1920-820',
};

const BannerSection = ({
  align = 'left',
  model,
  banner,
  special,
  newCarDetails,
  showOverlay = true,
}: BannerProps) => {
  const video = getImageLink(banner?.video);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [playing, setPlaying] = useState(true);
  const openLeadForm = useOpenFloatingCTA();

  const handleClick = () => {
    openLeadForm('test-drive');
  };

  const videoHandler = (control: string) => {
    if (control === 'play') {
      if (videoRef.current !== null) {
        videoRef.current.play();
      }

      setPlaying(true);
    } else if (control === 'pause') {
      if (videoRef.current !== null) {
        videoRef.current.pause();
      }
      setPlaying(false);
    }
  };

  return (
    <Box
       className={cn(Styles.bannerWrapper, {
        [Styles.topAlign as string]: align === 'top',
        [Styles.leftAlign as string]: align === 'left',
        [Styles.rightAlign as string]: align === 'right',
        [Styles.centerAlign as string]: align === 'centre_bottom',
        [Styles.withOverlay as string]: showOverlay,
      })}
    >
      <Box
        className={Styles.bannerSectionWrapper}
        paddingTop={8}
        paddingBottom={12}
        paddingX={6}
      >
        <Box className={Styles.headingElement} justifyContent={'center'}>
          <Heading
            as={!banner.video ? 'h1' : 'h2'}
            className={Styles.titleXl}
            color={'white'}
            marginBottom={4}
            textAlign={'center'}
            textTransform={'capitalize'}
          >
            {banner?.heading ?? special?.title}
          </Heading>
          <Heading
            marginBottom={4}
            className={Styles.titleXs}
            textAlign={'center'}
            as="h4"
            color={'white'}
          >
            {banner?.subheading}
          </Heading>
        </Box>
        <Stack gap={1} width={'max-content'} marginX={'auto'}>
          <Flex gap={{ base: '2' }} marginBottom={6} justifyContent={'center'}>
            {special?.monthly_repayment || special?.sale_price_incl_vat ? (
              <Text color={'white'} marginTop={'6px'}>
                From
              </Text>
            ) : null}
            <Heading
              as="h2"
              className={Styles.titleText}
              color={'white'}
              marginBottom={6}
            >
              {special?.monthly_repayment ? (
                <>
                  <PriceFormat prefix="R" value={special?.monthly_repayment} />
                  {' p/m'}
                </>
              ) : special?.sale_price_incl_vat ? (
                <PriceFormat prefix="R" value={special?.sale_price_incl_vat} />
              ) : special?.cashback_amount ? (
                <PriceFormat prefix="R" value={special?.cashback_amount} />
              ) : null}
            </Heading>
          </Flex>
        </Stack>
        <Flex
          gap={4}
          width={'max-content'}
          marginX={'auto'}
          direction={'column'}
          justifyContent={'center'}
        >
          <Box display={'flex'} gap={2}>
            {banner?.show_book_test_drive && (
              <ButtonCustom
                btnText="Book a Test Drive"
                className="btnWhite"
                moduleClass={Styles.bannerCta}
                onClick={handleClick}
              />
            )}
            {banner?.source_code && (
              <ButtonCustom
                btnText="Apply for Finance"
                className="btnWhite"
                moduleClass={Styles.bannerCta}
                onClick={() =>
                  openLeadForm('finance', 'finance', {
                    type: 'new-car',
                    newCar: newCarDetails ?? { id: 0, main_title: '' }
                  })
                }
              />
            )}
          </Box>
          {banner?.show_call_to_action &&
            banner?.call_to_action_url &&
            banner?.call_to_action_text && (
              <LinkCustom
                href={banner?.call_to_action_url}
                className="btnOutlineWhite"
                moduleClass={Styles.bannerCta}
              >
                {banner?.call_to_action_text}
              </LinkCustom>
            )}
          {banner?.show_call_to_action_2 &&
            banner?.call_to_action_url_2 &&
            banner?.call_to_action_text_2 && (
              <LinkCustom
                href={banner?.call_to_action_url_2 ?? '#'}
                className="btnWhite"
                moduleClass={Styles.bannerCta}
              >
                {banner?.call_to_action_text_2}
              </LinkCustom>
            )}
        </Flex>
        <Box
          className={Styles.titleXs}
          color={'white'}
          marginBottom={6}
          textTransform={'capitalize'}
          marginTop={'10px'}
          textAlign={'center'}
        >
          <Text>{banner?.terms_and_conditions}</Text>
        </Box>
      </Box>

      <Box className={Styles.heroSwiper}>
        <Box className={Styles.slideDesktop}>
          <Box
            className={cn(
              Styles.heroSwiperWrap,
              video && Styles.heroSwiperWrapVideo,
            )}
          >
            {banner?.desktop_image && (
              <Box className={Styles.bannerImage}>
                <CustomImage
                  imageKeys={imageKey}
                  imageSrc={getImageLink(banner?.desktop_image) ?? ''}
                  alt={`kia-${model}`}
                  width={1440}
                  height={817}
                  loading="eager"
                />
              </Box>
            )}

            {video && (
              <Box className={Styles.videoPlayer}>
                <video
                  playsInline
                  ref={videoRef}
                  className={Styles.videoPlayerFrame}
                  preload="metadata"
                  autoPlay
                  muted
                  loop
                >
                  <source src={video} type="video/mp4"></source>
                </video>
              </Box>
            )}
            <Container
              className={Styles.heroBannerContentWrapper}
              maxW={'1280'}
              width={'100%'}
              zIndex={4}
            >
              <Box className={Styles.heroBannerContent}>
                <Box className={Styles.headingElement}>
                  <Heading
                    as="h1"
                    className={Styles.titleXl}
                    color={'white'}
                    marginBottom={6}
                    textTransform={'capitalize'}
                  >
                    {banner?.heading}
                  </Heading>
                  <Box className={Styles.headingElement}>
                    <Heading
                      as="h2"
                      textAlign={
                        align === 'centre_bottom' || align === 'top'
                          ? 'center'
                          : 'inherit'
                      }
                      className={Styles.titleXs}
                      color={'white'}
                      marginBottom={6}
                      textTransform={'capitalize'}
                    >
                      {banner?.subheading}
                    </Heading>
                  </Box>
                </Box>
                <Flex gap={{ base: '2' }} className={Styles.bannerFeatures}>
                  {special?.monthly_repayment ||
                  special?.sale_price_incl_vat ? (
                    <Text color={'white'} className={Styles.verticleFromText}>
                      From
                    </Text>
                  ) : null}
                  <Heading
                    as="h2"
                    className={Styles.titleText}
                    color={'white'}
                    marginBottom={6}
                  >
                    {special?.monthly_repayment ? (
                      <>
                        <PriceFormat
                          prefix="R"
                          value={special?.monthly_repayment}
                        />
                        {' p/m'}
                      </>
                    ) : special?.sale_price_incl_vat ? (
                      <PriceFormat
                        prefix="R"
                        value={special?.sale_price_incl_vat}
                      />
                    ) : special?.cashback_amount ? (
                      <PriceFormat
                        prefix="R"
                        value={special?.cashback_amount}
                      />
                    ) : null}
                  </Heading>
                </Flex>
                {align === 'centre_bottom' && (
                  <HStack
                    direction={'row'}
                    alignItems={'center'}
                    justifyContent={'space-between'}
                    gap={10}
                    marginTop={'auto'}
                    marginBottom={12}
                  >
                    {Array.isArray(banner?.key_features) &&
                      banner?.key_features?.length > 0 &&
                      banner?.key_features.map((feature, index) => (
                        <React.Fragment key={index}>
                          <Box
                            textAlign={'center'}
                            color={'white'}
                          >
                            <Text as="b" fontSize="2xl">
                              {feature?.text}
                            </Text>
                          </Box>
                          {banner?.key_features &&
                            index !== banner?.key_features.length - 1 && (
                              <Divider
                                orientation="vertical"
                                borderColor={'gray.300'}
                                h={{ base: '80px', sm: '60px', md: '58px' }}
                              />
                            )}
                        </React.Fragment>
                      ))}
                  </HStack>
                )}
                <Flex
                  gap={4}
                  className={Styles.bannerButtons}
                  pb={align === 'centre_bottom' ? '20px' : '0px'}
                >
                  {banner?.show_book_test_drive && (
                    <ButtonCustom
                      btnText="Book a Test Drive"
                      className="btnWhite"
                      moduleClass={Styles.bannerCta}
                      onClick={handleClick}
                    />
                  )}
                  {banner?.source_code && (
                    <ButtonCustom
                      btnText="Apply for Finance"
                      className="btnWhite"
                      moduleClass={Styles.bannerCta}
                      onClick={() =>
                        openLeadForm('finance', 'finance', {
                          type: 'new-car',
                          newCar: newCarDetails ?? { id: 0, main_title: '' }
                        })
                      }
                    />
                  )}
                  {banner?.show_call_to_action &&
                    banner?.call_to_action_text &&
                    banner?.call_to_action_url && (
                      <LinkCustom
                        href={banner?.call_to_action_url}
                        className="btnOutlineWhite"
                        moduleClass={Styles.bannerCta}
                      >
                        {banner?.call_to_action_text}
                      </LinkCustom>
                    )}
                  {banner?.show_call_to_action_2 &&
                    banner?.call_to_action_text_2 &&
                    banner?.call_to_action_url_2 && (
                      <LinkCustom
                        href={banner?.call_to_action_url_2 ?? '#'}
                        className="btnOutlineWhite"
                        moduleClass={Styles.bannerCta}
                      >
                        {banner?.call_to_action_text_2}
                      </LinkCustom>
                    )}
                </Flex>
                <Box className={Styles.headingElement}>
                  <Text
                    className={Styles.titleXs}
                    color={'white'}
                    marginBottom={6}
                    textTransform={'capitalize'}
                    marginTop={'10px'}
                  >
                    {banner?.terms_and_conditions}
                  </Text>
                </Box>
              </Box>
              {video && (
                <Box className={Styles.videoPlayerController}>
                  <Stack gap={2.5} marginEnd={2}>
                    {playing ? (
                      <Flex
                        alignItems={'center'}
                        onClick={() => videoHandler('pause')}
                        cursor={'pointer'}
                      >
                        <Image
                          alt="play"
                          title='play'
                          height={16}
                          width={16}
                          src={PlayIconDisable}
                        />
                        <Image
                          alt="pause"
                          title="pause"
                          height={16}
                          width={16}
                          src={PauseIcon}
                        />
                        <Text
                          as="b"
                          lineHeight={'1'}
                          mt={0.5}
                          fontSize="xs"
                          color={'white'}
                        >
                          PAUSE
                        </Text>
                      </Flex>
                    ) : (
                      <Flex
                        alignItems={'center'}
                        onClick={() => videoHandler('play')}
                        cursor={'pointer'}
                      >
                        <Image
                          alt="play"
                          title="play"
                          height={16}
                          width={16}
                          src={PlayIcon}
                        />
                        <Image
                          alt="pause"
                          title="pause"
                          height={16}
                          width={16}
                          src={PauseIconDisable}
                        />
                        <Text
                          as="b"
                          lineHeight={'1'}
                          mt={0.5}
                          fontSize="xs"
                          color={'white'}
                        >
                          PLAY
                        </Text>
                      </Flex>
                    )}
                  </Stack>
                </Box>
              )}
            </Container>
          </Box>
        </Box>
        <Box className={Styles.slideMobile}>
          {banner?.mobile_image || banner?.desktop_image ? (
            <Box position={'relative'}>
              <CustomImage
                imageKeys={imageKey}
                imageSrc={
                  getImageLink(
                    banner?.mobile_image ?? banner?.desktop_image,
                  ) ?? ''
                }
                alt={`kia-${model}`}
                width={1440}
                height={817}
                loading="eager"
              />
            </Box>
          ) : (
            video && (
              <Box className={Styles.videoPlayer}>
                <Box className={Styles.videoPlayerController}>
                  <Stack gap={2.5} marginEnd={2}>
                    {playing ? (
                      <Flex
                        alignItems={'center'}
                        onClick={() => videoHandler('pause')}
                        cursor={'pointer'}
                      >
                        <Image
                          alt="play"
                          title="play"
                          height={16}
                          width={16}
                          src={PlayIconDisable}
                        />
                        <Image
                          alt="pause"
                          title="pause"
                          height={16}
                          width={16}
                          src={PauseIcon}
                        />
                        <Text
                          as="b"
                          lineHeight={'1'}
                          mt={0.5}
                          fontSize="xs"
                          color={'white'}
                        >
                          PAUSE
                        </Text>
                      </Flex>
                    ) : (
                      <Flex
                        alignItems={'center'}
                        onClick={() => videoHandler('play')}
                        cursor={'pointer'}
                      >
                        <Image
                          alt="play"
                          title="play"
                          height={16}
                          width={16}
                          src={PlayIcon}
                        />
                        <Image
                          alt="pause"
                          title="pause"
                          height={16}
                          width={16}
                          src={PauseIconDisable}
                        />
                        <Text
                          as="b"
                          lineHeight={'1'}
                          mt={0.5}
                          fontSize="xs"
                          color={'white'}
                        >
                          PLAY
                        </Text>
                      </Flex>
                    )}
                  </Stack>
                </Box>
                <video
                  playsInline
                  ref={videoRef}
                  className={Styles.videoPlayerFrame}
                  preload="metadata"
                  autoPlay
                  muted
                  loop
                >
                  <source src={video} type="video/mp4"></source>
                </video>
              </Box>
            )
          )}
          {Array.isArray(banner?.key_features) &&
            banner?.key_features?.length > 0 &&
            align === 'centre_bottom' && (
              <Stack
                alignItems={'center'}
                justifyContent={'center'}
                gap={4}
                padding={14}
                backgroundColor={'#f8f8f8'}
              >
                {Array.isArray(banner?.key_features) &&
                  banner?.key_features?.length > 0 &&
                  banner?.key_features.map((feature, index) => (
                    <React.Fragment key={index}>
                      <Box textAlign={'center'} color={'white'}>
                        <Text as="b" fontSize="2xl" color="gray.600">
                          {feature?.text}
                        </Text>
                      </Box>
                      {banner?.key_features &&
                        index !== banner?.key_features.length - 1 && (
                          <Divider
                            orientation="horizontal"
                            borderColor={'gray.600'}
                          />
                        )}
                    </React.Fragment>
                  ))}
              </Stack>
            )}
          </Box>
       </Box>
    </Box>
  );
};

export default BannerSection;
